import {Inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {BookingProviderDetails} from '../types/booking-provider-details';
import {BookingServiceDetail} from '../../../types/BookingServiceDetail';
import {BookingStaffMember} from '../types/booking-staff-member';
import {BookingV2ApiService} from './booking-v2-api.service';
import {BookingMetadata} from '../types/booking-metadata';
import {ToolsService} from '../../../services/tools.service';
import {MetaTag} from '../../../types/MetaTag';
import {MetaTypesEnum} from '../../../enums/SeoEnums';
import {SeoMetaService} from '../../../services/seo-meta.service';
import {BookingV2StateService} from './booking-v2-state.service';
import {BookingState} from '../types/booking-state';
import {
  BookingVirtualServiceGroupIDsEnum,
  StaffMembersSelectorEnum,
  SurveyAdditionalFieldsEnum,
  TherapistChoiceEnum,
} from '../enums/booking-enums';
import {LoaderQueueItem} from '../../../types/LoaderQueueItem';
import {catchError, filter, finalize, take} from 'rxjs/operators';
import {LoaderService} from '../../../services/loader.service';
import {CalendarResponse} from '../types/calendar-response';
import * as dayjs from 'dayjs';
import {Dayjs} from 'dayjs';
import 'dayjs/locale/sv';
import * as isoWeek from 'dayjs/plugin/isoWeek';
import {UntypedFormGroup} from '@angular/forms';
import {ApiHelper} from '../../../services/api.helper';
import {BookingV2ErrorHandlerService} from './booking-v2-error-handler.service';
import {BookingResponse} from '../types/booking-response';
import {BookingRequest} from '../types/booking-request';
import {SessionService} from '../../../services/session.service';
import {BookingProviderDetailsLink} from '../types/booking-provider-details-link';
import {BookingProviderDetailsLinkWithTreatments} from '../types/booking-provider-details-link-with-treatments';
import {forEach} from 'lodash-es';
import {isPlatformBrowser} from '@angular/common';
import {Profile} from '../../../types/MyPages';

interface LoadProviderQueryParams {
  providerId: number;
  serviceId: number;
}

interface LoadCalendarItemsQueryParams {
  providerId: number;
  bookingServiceId: string;
  bookingStaffMemberId: string;
  firstDay: string;
  lastDay: string;
}

interface LoadStaffListQueryParams {
  providerId: number;
  bookingServiceId: string;
}

@Injectable({
  providedIn: 'any',
})
export class BookingV2DataService implements OnDestroy {
  public providerDetails$: BehaviorSubject<BookingProviderDetails> = new BehaviorSubject(null);
  public staffMembers$: BehaviorSubject<Array<BookingStaffMember>> = new BehaviorSubject([]);
  public services$: BehaviorSubject<Array<BookingServiceDetail>> = new BehaviorSubject([]);
  public servicesGrouped$: BehaviorSubject<Array<BookingProviderDetailsLinkWithTreatments>> = new BehaviorSubject([]);
  public groups$: BehaviorSubject<Array<BookingProviderDetailsLink>> = new BehaviorSubject([]);
  public providerMeta$: BehaviorSubject<Array<BookingMetadata>> = new BehaviorSubject([]);
  public calendar$: BehaviorSubject<CalendarResponse> = new BehaviorSubject(null);
  public pageTitle$: BehaviorSubject<string> = new BehaviorSubject(null);
  public noTimeslotError$: BehaviorSubject<boolean> = new BehaviorSubject(null);
  public profile$: BehaviorSubject<Profile> = new BehaviorSubject(null);
  private loadedProviderParameters: LoadProviderQueryParams;
  private loadedCalendarParameters: LoadCalendarItemsQueryParams;
  private loadedStaffParameters: LoadStaffListQueryParams;
  private loadingProviderParameters: LoadProviderQueryParams;
  private loadingCalendarParameters: LoadCalendarItemsQueryParams;
  private loadingStaffParameters: LoadStaffListQueryParams;

  private state: BookingState;
  private subscriptions: Subscription[] = [];

  constructor(
    private apiHelper: ApiHelper,
    private tools: ToolsService,
    private bookingApi: BookingV2ApiService,
    private bookingState: BookingV2StateService,
    private metaService: SeoMetaService,
    private loaderService: LoaderService,
    private bookingErrorHandler: BookingV2ErrorHandlerService,
    private sessionService: SessionService,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    dayjs.locale('sv');
    dayjs.extend(isoWeek);
    this.resetAll();
    this.setListeners();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public resetAll(): void {
    this.providerDetails$.next(null);
    this.staffMembers$.next([]);
    this.services$.next([]);
    this.groups$.next([]);
    this.providerMeta$.next([]);
    this.calendar$.next(null);
    this.loadedProviderParameters = null;
    this.loadedCalendarParameters = null;
    this.loadedStaffParameters = null;
    this.loadingProviderParameters = null;
    this.loadingCalendarParameters = null;
    this.loadingStaffParameters = null;
    this.noTimeslotError$.next(null);
    this.profile$.next(null);
  }

  public resetProfile(): void {
    this.profile$.next(null);
    this.providerMeta$.next(this.providerMeta$.getValue());
  }

  public getServiceById(serviceId: string): BookingServiceDetail {
    return this.services$.getValue().find((service) => service.id === serviceId);
  }

  public getGroupById(id: number): BookingProviderDetailsLink {
    return this.groups$.getValue().find((item) => item.careServiceId === id);
  }

  public getStaffMemberById(therapistId: string): BookingStaffMember {
    return this.staffMembers$.getValue().find((therapist) => therapist.id === therapistId);
  }

  public getCalendarForNext60Days(): Observable<CalendarResponse> {
    //today + 30 days - today + 90 days
    let loadedFirstDay =
      !this.tools.isNil(this.loadedCalendarParameters) && !this.tools.isNil(this.loadedCalendarParameters.firstDay)
        ? this.loadedCalendarParameters.firstDay
        : this.state.calendarWeekStartDate;
    const firstDay = dayjs(loadedFirstDay).startOf('week').add(29, 'days');
    const lastDay = firstDay.add(59, 'days');
    return this.bookingApi.getBookingCalendar(
      this.state.providerId,
      this.state.selectedBookingServiceId,
      this.state.selectedStaffMemberId,
      firstDay.format('YYYY-MM-DD'),
      lastDay.format('YYYY-MM-DD'),
    );
  }

  public getCalendarForNext30Days(): Observable<CalendarResponse> {
    // today + 30 days - today + 60 days
    let loadedFirstDay =
      !this.tools.isNil(this.loadedCalendarParameters) && !this.tools.isNil(this.loadedCalendarParameters.firstDay)
        ? this.loadedCalendarParameters.firstDay
        : this.state.calendarWeekStartDate;
    const firstDay = dayjs(loadedFirstDay).startOf('week').add(29, 'days');
    const lastDay = firstDay.add(29, 'days');
    return this.bookingApi.getBookingCalendar(
      this.state.providerId,
      this.state.selectedBookingServiceId,
      this.state.selectedStaffMemberId,
      firstDay.format('YYYY-MM-DD'),
      lastDay.format('YYYY-MM-DD'),
    );
  }

  public reloadCalendar(): void {
    const providerDetails: BookingProviderDetails = this.providerDetails$.getValue();
    if (
      !this.tools.isNil(providerDetails) &&
      !this.tools.isNil(this.state.selectedBookingServiceId) &&
      !this.tools.isNil(this.state.selectedStaffMemberId) &&
      !this.tools.isNil(this.state.calendarWeekStartDate) &&
      !this.tools.isNil(this.state.calendarWeekEndDate)
    ) {
      const loadingParams = this.tools.clone(this.loadingCalendarParameters);

      const queryId: number = this.loaderService.generateQueueId();
      const queryItem: LoaderQueueItem = {
        id: queryId,
        title: '',
        description: '',
      };
      this.loaderService.addToQueue(queryItem);
      this.noTimeslotError$.next(null);
      this.subscriptions.push(
        this.bookingApi
          .getBookingCalendar(
            providerDetails.id,
            this.state.selectedBookingServiceId,
            this.state.selectedStaffMemberId,
            this.state.calendarWeekStartDate,
            this.state.calendarWeekEndDate,
          )
          .pipe(
            finalize(() => {
              this.loadingCalendarParameters = null;
              this.loaderService.removeFromQueue(queryId);
            }),
          )
          .subscribe(
            (result) => {
              this.calendar$.next(result);
              if (!this.tools.isNil(result) && !this.tools.isNil(result.items)) {
                let timeslots: number = 0;
                result.items.forEach((item) => {
                  timeslots += item.times.length;
                });
                const state = this.bookingState.state$.getValue();
                state.numberOfTimeslots = timeslots;
                this.bookingState.updateState(state);
              }
              this.loadedCalendarParameters = loadingParams;
            },
            (error) => console.error(error),
          ),
      );
    }
  }

  public checkFormAndSendBooking(
    surveyForm: UntypedFormGroup,
    phoneNumberFieldKey: string,
    successCallback: Function,
    invalidFormCallback: Function,
  ): void {
    const providerDetails = this.providerDetails$.getValue();
    const controls = surveyForm.controls;
    Object.keys(controls).forEach((key) => {
      controls[key].enable();
    });
    surveyForm.markAllAsTouched();
    if (
      surveyForm.valid &&
      !this.tools.isNil(this.state.selectedBookingServiceId) &&
      !this.tools.isNil(this.state.selectedStaffMemberId) &&
      !this.tools.isNil(providerDetails)
    ) {
      const queryId: number = this.loaderService.generateQueueId();
      const queryItem: LoaderQueueItem = {
        id: queryId,
        title: 'Din bokning verifieras...',
        description: 'Vänligen vänta, detta kan ta upp till en minut.',
      };
      this.loaderService.addToQueue(queryItem);

      this.preparePayloadAndSendBooking(surveyForm, phoneNumberFieldKey)
        .pipe(
          take(1),
          finalize(() => {
            this.loaderService.removeFromQueue(queryId);
          }),
          catchError((error) => {
            this.bookingState.updateStateProperty(
              'errorMessage',
              this.bookingErrorHandler.getErrorMessage(error.error),
            );
            if (
              !this.tools.isNil(
                error?.error?.additionInformation?.find((item) => item === 'booking_no_slots_available'),
              )
            ) {
              this.noTimeslotError$.next(true);
            }
            return this.apiHelper.handleError(error);
          }),
        )
        .subscribe((result: BookingResponse) => {
          this.noTimeslotError$.next(false);
          successCallback(result);
        });
    } else {
      invalidFormCallback();
    }
  }

  public preparePayloadAndSendBooking(
    surveyForm: UntypedFormGroup,
    phoneNumberFieldKey: string,
  ): Observable<BookingResponse> {
    const providerDetails = this.providerDetails$.getValue();
    const payload: BookingRequest = this.preparePayload(surveyForm, phoneNumberFieldKey);
    return this.bookingApi.sendBooking(
      providerDetails.id,
      this.state.selectedBookingServiceId,
      this.state.selectedStaffMemberId,
      this.state.serviceId,
      this.state.twoFactorAuthenticationRequired,
      payload,
    );
  }

  public prepareTherapistsList(bookingServiceId: string): Array<BookingStaffMember> {
    const filteredItems: Array<BookingStaffMember> = [];
    this.loadingStaffParameters = null;
    if (!this.tools.isNil(this.providerDetails$.getValue())) {
      this.loadedStaffParameters = {
        providerId: this.state.providerId,
        bookingServiceId: bookingServiceId,
      };
      this.providerDetails$.getValue().staff.forEach((staffMember) => {
        if (staffMember.services.findIndex((service) => service.id === bookingServiceId) !== -1) {
          filteredItems.push(staffMember);
        }
      });
      this.staffMembers$.next(filteredItems);
    }
    return filteredItems;
  }

  public setNoFollowMetaTag(): void {
    const metaTags: MetaTag[] = [];
    metaTags.push({
      value: 'noindex,nofollow',
      type: MetaTypesEnum.NAME,
      field: 'robots',
    });

    this.metaService.updateMetaTags(metaTags);
  }

  private preparePayload(surveyForm: UntypedFormGroup, phoneNumberFieldKey: string): BookingRequest {
    const payload: BookingRequest = {} as BookingRequest;
    const formData: Object = {};
    Object.keys(surveyForm.value).forEach((key) => {
      if (
        !((surveyForm.value[key] === '' || surveyForm.value[key] === null) && !this.isFieldRequired(key)) &&
        key !== SurveyAdditionalFieldsEnum.COUNTRY_CODE
      ) {
        formData[key] =
          key === phoneNumberFieldKey
            ? surveyForm.value[SurveyAdditionalFieldsEnum.COUNTRY_CODE] + surveyForm.value[key]
            : surveyForm.value[key];
      }
    });
    const dateOb: Dayjs = dayjs(this.state.selectedDateTime, this.bookingState.selectedDateTimeStorageFormat);
    payload.date = dateOb.format('YYYY-MM-DD');
    payload.time = dateOb.format('HH:mm');
    payload.formData = formData;
    payload.sessionId = this.sessionService.getSessionID();
    return payload;
  }

  private isFieldRequired(fieldKey: string): boolean {
    const metadata: BookingMetadata[] = this.providerMeta$.getValue();
    const field: BookingMetadata = metadata.find((item) => item.key === fieldKey);
    if (!this.tools.isNil(field)) {
      return field.validationRules.required;
    }
    return false;
  }

  private handleBookingState(state: BookingState): void {
    this.state = state;
    const loadingProviderParams: LoadProviderQueryParams = {
      providerId: state.providerId,
      serviceId: state.serviceId,
    };
    const loadingCalendarParams: LoadCalendarItemsQueryParams = {
      providerId: state.providerId,
      bookingServiceId: state.selectedBookingServiceId,
      bookingStaffMemberId: state.selectedStaffMemberId,
      firstDay: state.calendarWeekStartDate,
      lastDay: state.calendarWeekEndDate,
    };
    const loadingStaffParameters: LoadStaffListQueryParams = {
      providerId: state.providerId,
      bookingServiceId: state.selectedBookingServiceId,
    };

    if (
      !this.tools.isEqual(loadingProviderParams, this.loadedProviderParameters) &&
      !this.tools.isEqual(loadingProviderParams, this.loadingProviderParameters)
    ) {
      this.loadingProviderParameters = this.tools.clone(loadingProviderParams);
      this.reloadProvider();
      this.reloadProviderMeta();
    }
    if (
      !this.tools.isEqual(loadingCalendarParams, this.loadedCalendarParameters) &&
      !this.tools.isEqual(loadingCalendarParams, this.loadingCalendarParameters)
    ) {
      this.loadingCalendarParameters = this.tools.clone(loadingCalendarParams);
      this.reloadCalendar();
    }
    if (
      !this.tools.isNil(state.selectedBookingServiceId) &&
      !this.tools.isEqual(loadingStaffParameters, this.loadedStaffParameters) &&
      !this.tools.isEqual(loadingStaffParameters, this.loadingStaffParameters)
    ) {
      this.loadingStaffParameters = this.tools.clone(loadingStaffParameters);
      this.prepareTherapistsList(state.selectedBookingServiceId);
    }
  }

  private reloadProvider(): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: 'Söker efter lediga tider...',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);
    const queryState: BookingState = this.tools.clone(this.state);
    this.bookingApi
      .getBookingProviderDetails(queryState.providerId, queryState.serviceId)
      .pipe(
        finalize(() => {
          this.loadingProviderParameters = null;
          this.loaderService.removeFromQueue(queryId);
        }),
      )
      .subscribe((result) => this.handleProvider(result, queryState.providerId, queryState.serviceId));
  }

  private reloadProviderMeta(): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: '',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);
    this.bookingApi
      .getBookingMetadata(this.state.providerId)
      .pipe(finalize(() => this.loaderService.removeFromQueue(queryId)))
      .subscribe((result) => this.handleMetaData(result));
  }

  private handleProvider(result: BookingProviderDetails, providerId: number, serviceId: number): void {
    this.loadedProviderParameters = {
      providerId: providerId,
      serviceId: serviceId,
    };
    result = this.updateTherapistsListWithNecessaryData(result);
    result = this.updateUnasignedTreatments(result);
    this.providerDetails$.next(result);
    this.updateMetaTags(result);
    this.services$.next(result.activities);
    const groups: Array<BookingProviderDetailsLink> = this.getSortedGroupsFromServices(result.activities);
    this.servicesGrouped$.next(this.getGroupedServices(groups, result.activities));
    this.groups$.next(groups);
    this.staffMembers$.next(result.staff);
    this.reloadCalendar();

    if (isPlatformBrowser(this.platformId) && window['HubSpotConversations'] && ['HubSpotConversations']['widget']) {
      window['HubSpotConversations'].widget.refresh();
    }
  }

  private getGroupedServices(
    groups: Array<BookingProviderDetailsLink>,
    services: Array<BookingServiceDetail>,
  ): Array<BookingProviderDetailsLinkWithTreatments> {
    const grouped: Array<BookingProviderDetailsLinkWithTreatments> = [];
    forEach(groups, (group) => {
      grouped.push({
        ...group,
        services: services.filter((service) =>
          service.links.find((item) => item.careServiceId === group.careServiceId),
        ),
      });
    });
    return grouped;
  }

  private getSortedGroupsFromServices(services: Array<BookingServiceDetail>): Array<BookingProviderDetailsLink> {
    let groups: Array<BookingProviderDetailsLink> = [];
    services.forEach((service) => {
      groups = groups.concat(service.links);
    });
    return this.tools.uniqueBy(groups, 'careServiceId');
  }

  private handleMetaData(result: Array<BookingMetadata>): void {
    result = this.updateMetadataWithNecessaryData(result);
    this.providerMeta$.next(result);
  }

  private updateTherapistsListWithNecessaryData(providerDetails: BookingProviderDetails): BookingProviderDetails {
    const firstStaffMember: BookingStaffMember = providerDetails.staff[0];
    if (
      !firstStaffMember ||
      (firstStaffMember.id !== StaffMembersSelectorEnum.OPTION_ANY_ID &&
        firstStaffMember.id !== StaffMembersSelectorEnum.OPTION_NO_SELECTION)
    ) {
      providerDetails = this.addDefaultOptionToStaffList(providerDetails);
    }

    if (!this.tools.isNil(providerDetails.activities) && providerDetails.activities.length > 0) {
      providerDetails.staff.forEach((staff) => {
        if (staff.services.length === 0) {
          staff.services = providerDetails.activities;
        }
      });
    }

    return providerDetails;
  }

  private addDefaultOptionToStaffList(providerDetails: BookingProviderDetails): BookingProviderDetails {
    let allServices: BookingServiceDetail[] = !this.tools.isNil(providerDetails.activities)
      ? providerDetails.activities
      : [];
    providerDetails.staff.forEach((doctor: BookingStaffMember) => {
      allServices = [...allServices, ...doctor.services];
    });
    // get only unique services by ID
    allServices = [...new Map(allServices.map((item) => [item.id, item])).values()];

    if (providerDetails.therapistChoice && providerDetails.therapistChoice === TherapistChoiceEnum.REQUIRED) {
      const optionNone: BookingStaffMember = {} as BookingStaffMember;
      optionNone.id = StaffMembersSelectorEnum.OPTION_NO_SELECTION;
      optionNone.name = '-';
      optionNone.services = allServices;
      providerDetails.staff.unshift(optionNone);
    } else {
      const optionAny: BookingStaffMember = {} as BookingStaffMember;
      optionAny.id = StaffMembersSelectorEnum.OPTION_ANY_ID;
      optionAny.name = 'Valfri';
      optionAny.services = allServices;
      providerDetails.staff.unshift(optionAny);
    }
    return providerDetails;
  }

  private updateMetaTags(providerDetails): void {
    const metaTags: MetaTag[] = [];
    const title = `Boka en tid hos ${providerDetails ? providerDetails.name : ''}`;

    this.pageTitle$.next(title);
    metaTags.push({
      value: title,
      type: 'title',
      field: 'title',
    });

    metaTags.push({
      value: title,
      type: 'property',
      field: 'og:title',
    });

    this.metaService.updateMetaTags(metaTags);
  }

  private setListeners(): void {
    this.subscriptions.push(
      this.bookingState.state$
        .pipe(filter((res) => res.providerId !== null))
        .subscribe((result) => this.handleBookingState(result)),
    );
  }

  private updateMetadataWithNecessaryData(result: Array<BookingMetadata>): Array<BookingMetadata> {
    if (result && result.length > 0) {
      result.forEach((item: BookingMetadata) => {
        item.label = item.label.replace('*', '');
        if (item.validationRules && item.validationRules.required === true) {
          item.label = item.label + '*';
        }
      });
    }
    return result;
  }

  private updateUnasignedTreatments(result: BookingProviderDetails): BookingProviderDetails {
    if (result.activities && result.activities.length > 0) {
      result.activities.forEach((activity: BookingServiceDetail) => {
        if ((activity.links && activity.links.length === 0) || !activity.links) {
          activity.links = [
            {
              careServiceId: BookingVirtualServiceGroupIDsEnum.OTHERS,
              careServiceName: 'Ytterligare Behandlingar',
            },
          ];
        }
      });
    }
    return result;
  }
}
